import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Camera } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { take } from 'es-toolkit';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import { required } from '@/utils/validation';

const MAX_IMAGE_UPLOAD_COUNT = 5;

type Props = {
    isMobile: boolean;
    messageDetails: any;
    sellerId: number;
    subject: string;
    submitConsignment: Function;
    uiError: boolean;
    uiSubmitted: boolean;
};

const SendSellerConsignmentMessageModalDetails = (props: Props) => {
    const { formatMessage } = useIntl();
    const { isMobile = false, sellerId = 0, subject, submitConsignment, uiError, uiSubmitted } = props;

    const [imageUploads, setImageUploads] = React.useState([]);
    const [message, setMessage] = React.useState('');
    const [messageValid, setMessageValid] = React.useState(true);
    const [photoErrors, setPhotoErrors] = React.useState([]);
    const [photoValid, setPhotoValid] = React.useState(true);

    const onValueChange = ({ target }: any) => {
        const { id, value } = target;

        if (id === 'message') {
            setMessage(value);
        }
    };

    const onSubmit = React.useCallback(() => {
        const valid = required(message) && message.length >= 30;
        setMessageValid(valid);
        setPhotoErrors([]);
        setPhotoValid(true);

        if (valid) {
            submitConsignment({
                attachments: imageUploads,
                messageBody: message,
                sellerId,
                subject,
            });
        }
    }, [imageUploads, message, sellerId, subject, submitConsignment]);

    const onDrop = React.useCallback(
        (accepted: any, rejected: any) => {
            const photoErrors = [];
            const acceptedWithPreview = accepted.map((file) =>
                Object.assign(file, { preview: URL.createObjectURL(file) })
            );
            const attachments = imageUploads.concat(acceptedWithPreview);

            if (rejected.length > 0) {
                photoErrors.push('image_not_correct_format');
            }

            if (attachments.length > MAX_IMAGE_UPLOAD_COUNT) {
                photoErrors.push('only_five_images_allowed');
            }

            setImageUploads(take(attachments, MAX_IMAGE_UPLOAD_COUNT));
            setPhotoErrors(photoErrors);
            setPhotoValid(photoErrors.length === 0);
        },
        [imageUploads]
    );
    const disabled = imageUploads.length >= 5;

    // Create a dropzone for file uploads
    const {
        getInputProps,
        getRootProps,
        open: openFileDialog,
    } = useDropzone({
        // Accept all images
        accept: 'image/*',
        disabled,
        // Maximum size (10MB) per file
        maxSize: 10e6,
        noClick: true,
        onDrop,
    });

    const onDropLinkClick = () => {
        if (imageUploads.length >= 5) {
            setPhotoErrors(['only_five_images_allowed']);
            setPhotoValid(false);
        } else {
            openFileDialog();
        }
    };

    let msgErr = null;
    if (!messageValid) {
        msgErr = <FormattedMessage id="item_desc_too_short" />;
    }

    if (uiError) {
        msgErr = <FormattedMessage id="looks_like_something_went_wrong" />;
    }

    const clearAttachments = React.useCallback(() => {
        setImageUploads([]);
        setPhotoErrors([]);
        setPhotoValid(true);
    }, []);

    return (
        <FormContainer>
            <StyledForm onSubmit={onSubmit}>
                <ItemDescriptionGroup>
                    <FormLabelExtraSpace>
                        <FormattedMessage id="tell_us_about_your_thing" />
                    </FormLabelExtraSpace>
                    <FormLabel>
                        <FormattedMessage id="consignment_item_description" />
                    </FormLabel>
                    <StyledInput
                        aria-label={formatMessage({
                            id: 'consignment_item_description',
                        })}
                        errorMessage={msgErr}
                        id="message"
                        label={formatMessage({
                            id: 'consignment_item_description',
                        })}
                        onChange={onValueChange}
                        testid="consignment-text-input"
                        type="textarea"
                        valid={messageValid}
                    />
                </ItemDescriptionGroup>

                <ImageUploadContainer>
                    <FormLabel htmlFor="file-uploader">
                        <FormattedMessage id="add_photos" />
                    </FormLabel>
                    &nbsp;
                    <FormLabel>
                        <FormattedMessage id="add_photos_description" />
                    </FormLabel>
                    <FileDropzone
                        data-testid="image-dropzone"
                        {...getRootProps()}
                    >
                        <input
                            {...getInputProps()}
                            data-testid="image-uploader"
                            id="file-uploader"
                        />
                        <DropTargetInner>
                            {!isMobile && (
                                <Camera
                                    className="text-surface-500"
                                    style="fill"
                                />
                            )}
                            <div style={{ display: 'inline' }}>
                                {!isMobile && <span>Drag or&nbsp;</span>}
                                <Link onClick={onDropLinkClick}>
                                    <FormattedMessage id="click_to_upload" />
                                </Link>
                            </div>
                            <ImagePreview>
                                {imageUploads.map((f) => (
                                    <img
                                        alt={f.name}
                                        key={f.name}
                                        src={f.preview}
                                    />
                                ))}
                            </ImagePreview>
                            {imageUploads.length > 0 && (
                                <Link onClick={clearAttachments}>
                                    <span>Clear</span>
                                </Link>
                            )}
                        </DropTargetInner>
                    </FileDropzone>
                    <div>
                        {!photoValid &&
                            photoErrors.map((e, idx) => (
                                <ErrorMessage>
                                    <FormattedMessage
                                        id={e}
                                        key={idx}
                                    />
                                </ErrorMessage>
                            ))}
                    </div>
                </ImageUploadContainer>
            </StyledForm>
            <ButtonContainer>
                <StyledButton
                    data-testid="sendSellerConsignmentMessageModalSubmit"
                    disabled={uiSubmitted}
                    loading={uiSubmitted}
                    onClick={onSubmit}
                >
                    {formatMessage({ id: 'submit' })}
                </StyledButton>
            </ButtonContainer>
        </FormContainer>
    );
};

export default React.memo<Props>(SendSellerConsignmentMessageModalDetails);

const FormContainer = styled(BodyPrimary)`
    display: flex;
    flex-direction: column;
    align-items: center;
    letter-spacing: 1px;
    font-size: 11px;
    font-style: normal;
    text-align: left;
    padding: 40px 65px;
    overflow-y: auto;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding: 40px 30px 65px 30px;
    }
`;

const StyledForm = styled.form`
    width: 100%;
    font-weight: normal;
`;

const ItemDescriptionGroup = styled.div`
    min-height: 152px;
`;

const FormLabel = styled.label`
    text-transform: uppercase;
    width: 100% !important;
    font-weight: normal;
    color: ${({ theme }) => theme.colors.grey100};
    line-height: 1.45;
`;

const FormLabelExtraSpace = styled(FormLabel)`
    padding-right: 3px;
`;

const StyledInput = styled(Input).attrs(() => ({
    inputClassName: 'inputClassName',
}))`
    .inputClassName {
        min-height: 80px !important;
        max-height: 330px !important;
        resize: vertical !important;
    }
`;

const ImageUploadContainer = styled.div`
    margin: 18px 0 0;
    min-height: 260px;
`;

const FileDropzone = styled.div`
    border: solid 1px ${({ theme }) => theme.colors.grey400};
    min-height: 157px;
`;

const DropTargetInner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px;
    min-height: 155px;
    justify-content: center;

    span {
        font-family: ${({ theme }) => theme.typography.fontPrimary};
        font-size: 14px;
        letter-spacing: 0.5px;
    }
`;

const ImagePreview = styled.div`
    text-align: center;
    margin-top: 10px;

    img {
        max-height: 52px;
        max-width: 85px;
        padding: 5px 2.5px;
    }
`;

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.colors.red100} !important;
`;

const ButtonContainer = styled.div`
    text-align: center;
    width: 100%;
`;

const StyledButton = styled(Button)`
    &&& {
        display: inline-flex;
        width: 264px;
        @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
            width: 100%;
        }
    }
`;
